import React, { useState, useRef, useEffect } from 'react';
import TodoKanbanCard from '../TodoKanbanCard/TodoKanbanCard';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { Badge } from 'components/badges';

export default function TodoKanbanColumn({ column, items, onAction, onMove, onDrop, onReorder }) {
  const [collapsed, setCollapsed] = useState(false);
  const [isOver, setIsOver] = useState(false);
  const [dropIndex, setDropIndex] = useState(null);
  const containerRef = useRef(null);
  const dragOverTimerRef = useRef(null);

  useEffect(() => {
    const resetState = () => {
      setIsOver(false);
      setDropIndex(null);
      clearTimeout(dragOverTimerRef.current);
    };
    document.addEventListener('dragend', resetState);
    return () => {
      document.removeEventListener('dragend', resetState);
      clearTimeout(dragOverTimerRef.current);
    };
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsOver(true);

    clearTimeout(dragOverTimerRef.current);
    dragOverTimerRef.current = setTimeout(() => {
      setIsOver(false);
      setDropIndex(null);
    }, 100);

    const cards = containerRef.current?.querySelectorAll('[data-todo-id]');
    if (!cards?.length) {
      setDropIndex(0);
      return;
    }
    let index = items.length;
    for (let i = 0; i < cards.length; i++) {
      const rect = cards[i].getBoundingClientRect();
      if (e.clientY < rect.top + rect.height / 2) {
        index = i;
        break;
      }
    }
    setDropIndex(index);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    clearTimeout(dragOverTimerRef.current);
    setIsOver(false);
    const currentDropIndex = dropIndex;
    setDropIndex(null);

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.fromStatus === column.id) {
        const beforeItem = items[currentDropIndex];
        onReorder(data.todoId, beforeItem?._id ?? null);
      } else {
        const beforeItem = items[currentDropIndex];
        onDrop(data.todoId, column.id, beforeItem?._id ?? null);
      }
    } catch {
      const todoId = e.dataTransfer.getData('text/plain');
      if (todoId) onDrop(todoId, column.id);
    }
  };

  return (
    <div className="flex flex-col min-w-0">
      <button
        type="button"
        onClick={() => setCollapsed(prev => !prev)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors cursor-pointer"
      >
        {collapsed ? (
          <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
        )}
        <span className="text-sm font-semibold text-white">{column.label}</span>
        <Badge size="sm" color={column.color}>{items.length}</Badge>
      </button>

      {!collapsed && (
        <div
          ref={containerRef}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`mt-2 flex flex-col gap-2 min-h-[60px] rounded-lg p-2 transition-all duration-150 ${isOver ? 'border-2 border-dashed border-indigo-500/60 bg-indigo-500/5' : 'border-2 border-transparent bg-gray-900/30'}`}
        >
          {items.length === 0 && !isOver && (
            <div className="flex flex-col items-center justify-center py-6 border-2 border-dashed border-gray-700 rounded-lg">
              <p className="text-xs text-gray-500">Drop items here</p>
            </div>
          )}
          {items.map((todo, index) => (
            <React.Fragment key={todo._id}>
              {dropIndex === index && isOver && (
                <div className="h-0.5 rounded bg-indigo-500 mx-1" />
              )}
              <TodoKanbanCard
                todo={todo}
                currentStatus={column.id}
                onAction={onAction}
                onMove={onMove}
              />
            </React.Fragment>
          ))}
          {dropIndex === items.length && isOver && (
            <div className="h-0.5 rounded bg-indigo-500 mx-1" />
          )}
        </div>
      )}
    </div>
  );
}
